import { get } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import store from '../../../store';
import { selectActiveAccount } from '../../../store/accounts';
import AuthService from '../../../lib/auth/AuthService';

import { Locale, LocaleService } from './LocaleService';

export function createLocaleService(
  config: {
    /** The language the app picks by itself, where it has no session store to read it from. */
    resolveAppLanguage?: () => Promise<Locale>;
  } = {}
): LocaleService {
  const service: LocaleService = {
    useCurrentLocale: () => useTranslation().i18n.language as Locale,
    useChangeCurrentLocale: () => {
      const { i18n } = useTranslation();
      return newLocale => {
        void i18n.changeLanguage(newLocale);
        if ((i18n as any).IS_PUBLIC_INSTANCE) {
          // we want to store the language in local storage only if we are using the public translations
          // internal translations are currently for development purposes only and as such they should not interfere with the public language settings
          service
            .resolveAppLanguage()
            .then(accountLanguage => {
              if (accountLanguage) {
                // server language is loaded, we can set local storage against it
                if (accountLanguage === newLocale) {
                  // we are setting the same language as server language, remove from local storage, so it will now use server language all the time
                  localStorage.removeItem('atl.language');
                } else {
                  // user has changed the language, set it in local storage, so it will now use user selected language
                  localStorage.setItem('atl.language', newLocale);
                }
              } else {
                // server language is not loaded, set it in local storage for future use
                localStorage.setItem('atl.language', newLocale);
              }
            })
            .catch(() => {
              // If resolveAppLanguage fails, just set it in local storage for future use
              localStorage.setItem('atl.language', newLocale);
            });
        }
      };
    },
    useSupportedLanguages: scope => {
      return useMemo(
        () =>
          scope === 'internal'
            ? [
                // internal languages
                {
                  locale: 'en-GB',
                  label: 'English (GB)',
                  flag: '🇬🇧'
                },
                {
                  locale: 'en',
                  label: 'English (US)',
                  flag: '🇺🇸'
                }
                // {
                //   locale: 'zh',
                //   label: '简体中文',
                //   flag: '🇨🇳'
                // },
                // {
                //   locale: 'pl',
                //   label: 'Polski',
                //   flag: '🇵🇱'
                // }
              ]
            : [
                // public languages, for now we return the same as internal
                {
                  locale: 'en-GB',
                  label: 'English (GB)',
                  flag: '🇬🇧'
                },
                {
                  locale: 'en',
                  label: 'English (US)',
                  flag: '🇺🇸'
                },
                {
                  locale: 'zh',
                  label: '简体中文',
                  flag: '🇨🇳'
                },
                {
                  locale: 'pl',
                  label: 'Polski',
                  flag: '🇵🇱'
                }
              ],
        [scope]
      );
    },
    useLocalStorageLanguage: () => {
      if (typeof localStorage !== 'undefined') {
        return localStorage.getItem('atl.language');
      }
      return null;
    },
    resolveAppLanguage: () => {
      if (config.resolveAppLanguage) return config.resolveAppLanguage();
      return new Promise<Locale>((resolve, reject) => {
        const isLoggedIn = AuthService.isAuthenticated();

        const checkAndResolve = () => {
          try {
            const state = store.getState();
            const publicLocale =
              get(state, 'public_settings.settings.defaults_locale') ??
              get(state, 'public_settings.settings.defaultsLocale') ??
              get(state, 'auth.settings.settings.defaults_locale.value');

            if (!isLoggedIn) {
              if (publicLocale) {
                unsubscribe();
                resolve(publicLocale);
              }
              return; // Keep waiting for public settings
            }

            // Check if auth settings are loaded
            if (
              !(
                get(state, 'auth.settings.loggedIn') ||
                selectActiveAccount(state)
              )
            ) {
              return; // Keep waiting for auth settings
            }

            const userLocale = selectActiveAccount(state)?.preferredLanguage;

            unsubscribe();
            resolve((userLocale ?? publicLocale ?? 'en') as Locale);
          } catch (error) {
            unsubscribe();
            reject(error);
          }
        };

        const unsubscribe = store.subscribe(checkAndResolve);

        // Execute immediately after subscribing
        checkAndResolve();
      });
    }
  };
  return service;
}
